// Keyboard primitives shared by the shell and the feature screens (features never import src/app/**).
// Handlers match event.key (/ and ? need Shift on Nordic layouts). Nothing fires inside inputs, textareas,
// selects, contenteditable or during IME composition; single keys also wait while a dialog is open.
import { useEffect, useRef } from 'react'

/** True while the person is typing (a text field has focus, or an IME composition runs). */
export function typing(e: KeyboardEvent): boolean {
  if (e.isComposing || e.keyCode === 229) return true
  const el = e.target as HTMLElement | null
  return !!el && (el.matches?.('input, textarea, select') || el.isContentEditable)
}

function dialogOpen(): boolean {
  return !!document.querySelector('[aria-modal="true"], [cmdk-dialog]')
}

/** True when a single-key shortcut may fire for this event. */
export function singleKeyAllowed(e: KeyboardEvent): boolean {
  return !e.defaultPrevented && !e.metaKey && !e.ctrlKey && !e.altKey && !typing(e) && !dialogOpen()
}

/** Keys a mounted screen handles itself; the shell's default for that key then stays quiet. */
export const claimedKeys = new Map<string, number>()

/** A screen-level single-key shortcut with the same guards: useShortcut('l', () => openLogTouch()). */
export function useShortcut(key: string, handler: (e: KeyboardEvent) => void): void {
  const ref = useRef(handler)
  ref.current = handler
  useEffect(() => {
    claimedKeys.set(key, (claimedKeys.get(key) ?? 0) + 1)
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== key || !singleKeyAllowed(e)) return
      e.preventDefault()
      ref.current(e)
    }
    window.addEventListener('keydown', onKey)
    return () => {
      claimedKeys.set(key, (claimedKeys.get(key) ?? 1) - 1)
      window.removeEventListener('keydown', onKey)
    }
  }, [key])
}
